/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type {SettingName} from '../appSettings';

import * as React from 'react';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext, useEffect,
  useMemo,
  useState
} from "react";

import {DEFAULT_SETTINGS} from '../appSettings';
import { unified } from "unified";
import retextEnglish from "retext-english";
import retextReadability from "retext-readability";
import retextEquality from "retext-equality";
import retextIndefiniteArticle from "retext-indefinite-article";
import retextKeywords from "retext-keywords";
import retextPassive from "retext-passive";
import retextRepeatedWords from "retext-repeated-words";
import retextSimplify from "retext-simplify";
import retextIntensify from "retext-intensify";
import retextProfanities from "retext-profanities";
import retextRedundantAcronyms from "retext-redundant-acronyms";
import retextSentenceSpacing from "retext-sentence-spacing";
import retextSmartypants from "retext-smartypants";
import retextQuotes from "retext-quotes";
import retextStringify from "retext-stringify";
import { syllable } from "syllable";
import readingTime from 'reading-time/lib/reading-time';
import { assignColorsToSources } from '../../../../lib/helpers/helpers';
import { $getRoot } from "lexical";
/*
import {read} from 'to-vfile'
import {reporter} from 'vfile-reporter'
import retextSpell from 'retext-spell'
import dictionary from 'dictionary-en-gb'*/

type SettingsContextShape = {
  setOption: (name: SettingName, value: boolean) => void;
  settings: Record<SettingName, boolean>;
  content: any;
  stats: object;
  analyzed: Array<object>;
  colorMap: any;
  setContent: (content: string) => void;
  extractText: (content: string) => void;
  extractFullText: (content: object) => void;
  assignColorsToSources: (content: any) => void;
};

const Context: React.Context<SettingsContextShape> = createContext({
  setOption: (name: SettingName, value: boolean) => {
    return;
  },
  settings: DEFAULT_SETTINGS,
    content: {},
    stats: {},
    analyzed: [],
    colorMap: {},
    setContent: (content: string) => {},
    extractText: (content: string) => {},
    extractFullText: (content: object) => {},
    assignColorsToSources: (content: any) => {}
  }
);
export const analyzeText = async (text, startingOffset = 0) =>  {
  const result = await unified()
    .use(retextEnglish)
    .use(retextReadability)
    .use(retextEquality)
    .use(retextIndefiniteArticle)
    .use(retextKeywords)
    .use(retextPassive)
    .use(retextRepeatedWords)
    .use(retextSimplify)
    .use(retextIntensify)
    .use(retextProfanities)
    .use(retextRedundantAcronyms)
    .use(retextSentenceSpacing)
    .use(retextSmartypants)
    .use(retextQuotes)
    //.use(retextSpell, dictionary)
    .use(retextStringify).process(await text);

  const metrics = result.messages;
  metrics.forEach(metric => {
    metric.position.start.offset += startingOffset;
    metric.position.end.offset += startingOffset;
  });
  return metrics
}
export const SettingsContext = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [stats, setStats] = useState({});
  const [analyzed, setAnalyzed] = useState([]);
  const [content, setContent] = useState(localStorage.getItem('JOT_IT') || undefined)
  const setOption = useCallback((setting: SettingName, value: boolean) => {
    setSettings((options) => ({
      ...options,
      [setting as string]: value,
    }));
    if (DEFAULT_SETTINGS[setting] === value) {
      setURLParam(setting, null);
    } else {
      setURLParam(setting, value);
    }
  }, []);


  let globalOffset = 0;

  const extractText = (data) =>  {
    if (data.type === 'text' || data.type === 'HighlightedTextNode') {
      const nodeText = data.text;
      data.globalOffset = globalOffset;
      globalOffset += nodeText.length;
      return nodeText;
    } else if (Array.isArray(data.children)) {
      return data.children.map(child => extractText(child)).join(' ');
    }
    return '';
  }

  const handleSummarizeLast = useCallback(async () => {
    // @ts-ignore
    if(content && content.root){
      let offset = 0;
      // @ts-ignore
      const extractedTexts = content?.root?.children?.map(child => {
        const text = extractText(child);
        const result = { text, offset };
        offset += text.length;
        return result;
      });
      const analyzedResults = extractedTexts ? await Promise.all(extractedTexts.map(({text, offset}) => analyzeText(text, offset))) : [];
      const flattenedResults = analyzedResults.flat();
      setAnalyzed(flattenedResults);
    }
  },[content]);

  const handleSummarizeLastWorking2 = useCallback(async () => {
    // @ts-ignore
    if(content && content.root){
      // @ts-ignore
      const root = $getRoot();
      const textNodes = root.getAllTextNodes();
      const analyzedResults = await Promise.all(textNodes.map(async (node) => {
        const textContent = node.getTextContent();
        console.log('individual textContent', textContent)
        const analyzed = await analyzeText(textContent, 0);
        const color = analyzed && analyzed[0] ? colorMap[analyzed[0].source] : false;
        if (color) {
          return { color, ...analyzed[0] };
        }
        return null;
      }));
      const nonNullResults = analyzedResults.filter(result => result !== null).flat();
      setAnalyzed(nonNullResults);
    }
  },[content]);
  const handleSummarize = useCallback(async () => {
    // @ts-ignore
    if(content && content.root){
      // @ts-ignore
      const root = $getRoot();
      const textNodes = root.getAllTextNodes();
      const combinedText = textNodes.map(node => node.getTextContent()).join('.');
      console.log('combinedText', combinedText)
      const analyzed = await analyzeText(combinedText, 0);

      // Get color for each error
      const analyzedResults = analyzed.map(error => {
        const color = colorMap[error.source] || false;
        if (color) {
          return { color, ...error };
        }
        return null;
      });
      const nonNullResults = analyzedResults.filter(result => result !== null);
      setAnalyzed(nonNullResults);
    }
  },[content]);



  const colorMap = useMemo(() => assignColorsToSources(analyzed),[analyzed]);
  const extractFullText = (lexicalObject) =>  {
    let text = '';
    if (lexicalObject.root && lexicalObject.root.children) {
      lexicalObject.root.children.forEach(child => {
        if (child.children) {
          child.children.forEach(childText => {
            if (childText.text) {
              text += childText.text + ' ';
            }
          });
        }
      });
    }
    return text;
  }
  const getTextStats = useCallback(() =>  {
    const text = extractFullText(content);
    const words = text.split(' ').length - 1;
    const letters = text.replace(/[^a-z]/gi, '').length;
    const sentences = text.split('.').length - 1;
    const paragraphs = (text.match(/\n/g)||[]).length + 1;
    const readingStats = readingTime(text);
    const syllables = syllable(text);

    setStats({
      readability: `6/10\r\n(Good)`,  // For now, we'll just use a placeholder value.
      readingTime: readingStats.text,
      letters: letters,
      words: words,
      sentences: sentences,
      paragraphs: paragraphs,
      syllables: syllables
    });
  },[content, extractFullText])


  useEffect(  () => {
    if(content){
      handleSummarize()
      getTextStats()
      //console.log('content', content);
      //console.log('stats', stats);
    }
  },[content])

  const contextValue = useMemo(() => {
    return {setOption, settings, stats, analyzed, content, colorMap, setContent, extractText, extractFullText, analyzeText, assignColorsToSources};
  }, [setOption, settings, stats, analyzed, content, colorMap, setContent, extractText, extractFullText, analyzeText, assignColorsToSources]);
  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

export const useSettings = (): SettingsContextShape => {
  return useContext(Context);
};

function setURLParam(param: SettingName, value: null | boolean) {
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  if (value !== null) {
    if (params.has(param)) {
      params.set(param, String(value));
    } else {
      params.append(param, String(value));
    }
  } else {
    if (params.has(param)) {
      params.delete(param);
    }
  }
  url.search = params.toString();
  window.history.pushState(null, '', url.toString());
}
